import { useLDClient } from 'launchdarkly-react-client-sdk';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { coreapi, permissionsapi } from '../api';
import { App } from '../App';
import { FullPageLoader } from '../components';
import {
  AuthDatum,
  AuthEffectType,
  AuthStore,
  BrandMCC,
  BrandStore,
  getSavedProgress,
  RegistrationStore,
  updateAuth,
  updateAuthIndex,
  updateBrand,
  updateRegistration,
} from '../context';
import { App as GogurtApp } from '../gogurt/App';
import { history } from '../history';
import { useAsyncEffect, usePrevious, useToaster } from '../hooks';
import { isTokenExpired } from '../util/jwt.util';
import { buildLDContext } from '../util/launchdarkly.util';
import { isGogurtSource } from '../util/registration.util';

// Last-resort cap (ms) on waiting for LaunchDarkly to load flags for the
// identified context before we evaluate the redirect flag. Only trips when LD
// is unreachable; the normal path resolves as soon as identify() completes.
const LD_IDENTIFY_TIMEOUT_MS = 5000;

/**
 * Loads the following context stores via asynchronous effects:
 *  - Registration
 *  - Auth
 *  - Brand
 * A full page loading spinner is displayed until the loading completes.
 * Then either the legacy UI or gogurt UI is rendered based on
 * 'isGogurtSource()' or saved progress in localStorage.
 * Both UIs use the same context stores except for 'PathMemory' (used by legacy UI)
 * and 'Progress' (used by gogurt UI).
 */
export const AppFlowPicker = () => {
  const { pathname, search } = useLocation();
  const searchRef = useRef(search);
  searchRef.current = search;
  const hasCheckedLDRef = useRef(false);
  const ldClient = useLDClient();
  const { toaster, toast } = useToaster();
  const {
    dispatch: brandDispatch,
    state: { brand },
  } = useContext(BrandStore);
  const {
    dispatch: authDispatch,
    state: { auth, status: authStatus, effect: authEffect },
  } = useContext(AuthStore);
  const {
    dispatch: registrationDispatch,
    state: { registration },
  } = useContext(RegistrationStore);

  const authBrand = auth?.brand || null;
  const authToken = auth?.token;
  const authIndex = auth?.index;
  const prevAuthIndex = usePrevious(authIndex);
  const savedProgress = getSavedProgress();
  const isGogurt = registration
    ? isGogurtSource(registration)
    : savedProgress != null;
  const isPendingReSignature =
    registration?.signature_status === 'PENDING RE-SIGNATURE';
  const merchantId = auth?.merchant?.id;
  const tokenExpired = authToken ? isTokenExpired(authToken) : false;

  // useCallbacks

  const fetchRegistrationAndBrand = useCallback(async () => {
    try {
      const registration = await coreapi.get(authToken!, '/team/registration');
      registrationDispatch(updateRegistration(registration));

      const [permissions] = await permissionsapi.get(authToken!, '/self');
      let mcc: BrandMCC[] | null = null;
      try {
        mcc = await permissionsapi.get(authToken!, '/self/mcc');
      } catch (error) {
        console.error('Unable to fetch mcc information');
      }
      brandDispatch(updateBrand(permissions.brand, authBrand, mcc));
    } catch (error) {
      toaster(toast.error(error, 'Network Error'));
    }
  }, [
    authToken,
    authBrand,
    registrationDispatch,
    brandDispatch,
    toaster,
    toast,
  ]);

  // useEffects

  useEffect(() => {
    if (tokenExpired) {
      history.push('/logout');
    }
  }, [tokenExpired]);

  useEffect(() => {
    if (
      isPendingReSignature &&
      !(pathname.includes('application') || pathname.includes('tracker'))
    ) {
      /**
       * If the registration is waiting for a re-signature, and route does not include application
       * redirect to application review page with the is_pending_re_signature query params
       * where the signature modal would be opened
       */
      history.push(`/application/review?is_pending_re_signature=true`);
    }
  }, [isPendingReSignature, pathname]);

  useAsyncEffect(async () => {
    // fetch self on mount
    if (authToken) {
      try {
        const authData: AuthDatum[] = await coreapi.get(
          authToken,
          '/self/list',
        );
        authDispatch(updateAuth(authData));

        if (auth?.merchant?.id) {
          const index = authData.findIndex(
            authDatum => authDatum.merchant?.id === merchantId,
          );
          if (index > -1) {
            authDispatch(updateAuthIndex(index));
          }
        }
      } catch (error) {
        toaster(toast.error(error, 'Network Error'));
      }
    }
  }, []);

  useEffect(() => {
    if (
      hasCheckedLDRef.current ||
      !ldClient ||
      !authToken ||
      tokenExpired ||
      !auth?.user?.id
    )
      return;
    hasCheckedLDRef.current = true;

    // Evaluate the redirect flag against the IDENTIFIED merchant context.
    // On a cold first load (fresh SSO) the LDProvider only mounts once auth
    // resolves, so variation() can run before LD has loaded the flag values for
    // this user/merchant — it then returns the default (false), which left
    // forwarded (e.g. source=deeplink) users on the merchant picker instead of
    // redirecting. A warm reload hydrates auth from localStorage, so the context
    // is ready immediately and the redirect fires — hence the inconsistency.
    // identify() reloads flags for THIS context and its promise resolves only
    // once those values are available, so variation() below reads the real flag
    // value on first load too. Same context as FeatureProvider.
    const context = buildLDContext(auth);

    // Safety net: never trap the user on the loader if identify() never settles
    // (e.g. LD unreachable). On timeout we fall through and evaluate on whatever
    // LD has — the default (false) keeps the user in this app over redirecting.
    let timeoutId: ReturnType<typeof setTimeout>;
    const timeout = new Promise<void>(resolve => {
      timeoutId = setTimeout(resolve, LD_IDENTIFY_TIMEOUT_MS);
    });

    Promise.race([ldClient.identify(context).then(() => undefined), timeout])
      .catch(err => {
        // Swallow failure so .then() always runs — variation() on partially
        // loaded flag data is better than silently skipping the check.
        console.warn('LaunchDarkly identify failed', err);
      })
      .then(() => {
        clearTimeout(timeoutId);
        // Clear the one-shot marker immediately so it never persists across
        // early returns (expired token, missing env var, flag off).
        const authMethod = localStorage.getItem('selfreg.authmethod');
        localStorage.removeItem('selfreg.authmethod');

        // Re-check expiry: token may have expired while waiting on LD
        if (isTokenExpired(authToken)) {
          fetchRegistrationAndBrand();
          return;
        }

        const shouldRedirect = ldClient.variation(
          'redirect-to-new-signup-app',
          false,
        );
        const newSignupUrl = process.env.REACT_APP_NEW_SIGNUP_URL;
        if (shouldRedirect) {
          if (!newSignupUrl) {
            console.warn(
              'redirectToNewSignupApp is true but REACT_APP_NEW_SIGNUP_URL is not set',
            );
            fetchRegistrationAndBrand();
            return;
          }
          const params = new URLSearchParams(searchRef.current);
          params.delete('jwt');
          if (authMethod === 'magiclink') {
            params.set('source', 'magiclink');
          }
          const forwardedParams = params.toString()
            ? `&${params.toString()}`
            : '';
          window.location.replace(
            `${newSignupUrl}/#/sso?jwt=${authToken}${forwardedParams}`,
          );
        } else {
          fetchRegistrationAndBrand();
        }
      });
    // fetchRegistrationAndBrand is intentionally omitted: this is a one-shot redirect
    // check when LD and the token first become available. Re-running on auth context
    // changes (authBrand, dispatchers) is already handled by the merchant-switch effect.
  }, [ldClient, authToken, tokenExpired]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // if user switches merchants
    if (
      authEffect?.type === AuthEffectType.UPDATE_AUTH_INDEX &&
      authIndex !== prevAuthIndex
    ) {
      fetchRegistrationAndBrand();
    }
  }, [authEffect, authIndex, prevAuthIndex, fetchRegistrationAndBrand]);

  if (!brand || authStatus === 'notauthed' || tokenExpired || !registration) {
    // Display page loader while the request for permissions & brand theme is still pending
    // OR as a placeholder before we perform logout-redirect effect (triggered by expired auth).
    return <FullPageLoader />;
  }

  return isGogurt ? <GogurtApp /> : <App />;
};
